// @flow

import moment from 'moment';

/**
 * Trial display utilities.
 * Trial days come from product.metadata.trial_days (not the legacy Price field).
 */

/**
 * Get trial days from a product's metadata.
 * Reads product.metadata.trial_days, parses to int, returns null if absent/0.
 */
export const getTrialDays = (product: ?Object): number | null => {
  if (!product?.metadata?.trial_days) return null;
  const days = parseInt(product.metadata.trial_days, 10);
  if (isNaN(days) || days <= 0) return null;
  return days;
};

/**
 * Check if a product has a trial.
 */
export const hasTrial = (product: ?Object): boolean => {
  return getTrialDays(product) !== null;
};

/**
 * Calculate trial end date from today.
 */
export const getTrialEndDate = (product: ?Object): ?Object => {
  const days = getTrialDays(product);
  if (days === null) return null;
  return moment().add(days, 'days');
};

/**
 * Format trial end date for display.
 * e.g., "March 15, 2026"
 */
export const formatTrialEndDate = (product: ?Object): string => {
  const endDate = getTrialEndDate(product);
  if (!endDate) return '';
  return endDate.format('MMMM D, YYYY');
};

/**
 * Get CTA text from product metadata.
 * Prioritizes product.metadata.cta_text; falls back to defaultText.
 */
export const getTrialCTAText = (product: ?Object, defaultText: string = 'Choose & Continue'): string => {
  if (product?.metadata?.cta_text) return product.metadata.cta_text;
  return defaultText;
};

/**
 * Get "due today" amount.
 * Returns 0 if trial, calculated price otherwise.
 */
export const getDueToday = (product: ?Object, totalPrice: number): number => {
  if (hasTrial(product)) return 0;
  return totalPrice;
};

/**
 * Build trial payload for charge API.
 * If trial exists, returns { trialDays: N } (simple integer).
 * Backend passes this as trial_period_days to stripe.subscriptions.create().
 */
export const buildTrialPayload = (product: ?Object): Object => {
  const days = getTrialDays(product);
  if (days === null) return {};
  return { trialDays: days };
};
